import { useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useTranslation } from "react-i18next";
import {
  canAccessDataCorrection,
  canAccessDataConsumption,
  canAccessDataSet,
  canAccessDataSources,
  canAccessExpressions,
  canAccessMigrations,
  canAccessUsers,
} from "../routing/authorizationhandlers";
import { logout, setCurrentUser } from "../redux/slices/currentuserslice";
import {
  setCurrentTheme,
  setUserTheme,
} from "../redux/slices/currentthemeslice";
import {
  setSideBarDetailVisible,
  setSideBarVisible,
} from "../redux/slices/appslice";
import { setAppearance } from "../redux/slices/appearanceslice";
import { hideAppDialog, setAppDialog } from "../redux/slices/appdialogslice";
import { ThemesSelector } from "./themesselector";
import { ChangePasswordForm } from "./changepasswordform";
import { themes } from "../sass/themes";

const SettingsItem = ({ icon, label, onClick, to, external = false }) => {
  const content = (
    <>
      <span className={`k-icon k-i-${icon}`} aria-hidden="true" />
      <span>{label}</span>
      <span className="k-icon k-i-chevron-right" aria-hidden="true" />
    </>
  );

  if (to) {
    return external ? (
      <a
        className="mobile-settings-item"
        href={to}
        target="_blank"
        rel="noreferrer"
      >
        {content}
      </a>
    ) : (
      <NavLink className="mobile-settings-item" to={to}>
        {content}
      </NavLink>
    );
  }

  return (
    <button className="mobile-settings-item" type="button" onClick={onClick}>
      {content}
    </button>
  );
};

const MobileSettingsView = ({ onClose, onChangePassword }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { switcher } = useThemeSwitcher();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const themeId = useRef(currentTheme.id);
  const sideBarType = useRef(currentUser.SideBarType);

  const navigateAndClose = (path) => {
    onClose();
    navigate(path);
  };

  const handleAppearance = () => {
    dispatch(
      setAppDialog({
        isVisible: true,
        closable: true,
        title: t("currentuser.appearance"),
        className: "themes-selector",
        content: (
          <ThemesSelector
            onThemeChange={(event) => {
              themeId.current = event.value;
            }}
            onIsRightToLeftChange={() => {}}
            onSideBarChange={(event) => {
              sideBarType.current = event.value;
            }}
          />
        ),
        actions: {
          yesButton: {
            title: t("currentuser.apply"),
            handler: () => {
              if (currentTheme.processing) return;

              dispatch(
                setUserTheme(
                  themeId.current,
                  appearance.isRightToLeft,
                  sideBarType.current,
                  currentUser.LanguageId,
                  t,
                ),
              ).then(
                () => {
                  dispatch(
                    setCurrentUser({
                      ...currentUser,
                      SideBarType: sideBarType.current,
                    }),
                  );
                  dispatch(setCurrentTheme(themes[themeId.current.toString()]));
                  dispatch(
                    setAppearance({
                      isRightToLeft: appearance.isRightToLeft,
                    }),
                  );
                  dispatch(setSideBarVisible(sideBarType.current !== 1));
                  dispatch(setSideBarDetailVisible(false));
                  switcher({ theme: themes[themeId.current.toString()] });
                  dispatch(hideAppDialog());
                },
                () => {},
              );
            },
          },
          noButton: { title: t("currentuser.close") },
        },
      }),
    );
    onClose();
  };

  const handleLogout = () => {
    dispatch(logout({})).then((action) => {
      if (action.payload?.succeeded) {
        onClose();
        navigate("/", { replace: true });
      }
    });
  };

  const dataItems = [
    {
      visible: canAccessDataSet(currentUser),
      label: t("header.datasets"),
      icon: "folder",
      path: "/datasets",
    },
    {
      visible: canAccessDataSources(currentUser),
      label: t("header.datasources"),
      icon: "data",
      path: "/datasources",
    },
    {
      visible: canAccessExpressions(currentUser),
      label: t("header.expressions"),
      icon: "data-sql",
      path: "/expressions",
    },
    {
      visible: canAccessDataCorrection(currentUser),
      label: t("header.datacorrection"),
      icon: "edit-tools",
      path: "/datacorrection",
    },
    {
      visible: canAccessMigrations(currentUser),
      label: t("header.migrations"),
      icon: "wrench",
      path: "/migrations",
    },
  ];

  return (
    <>
      <aside
        id="mobile-settings-view"
        className="mobile-settings-view"
        aria-label={t("mobilebottomnavigation.settings")}
      >
        <header className="mobile-settings-header">
          <h1>{t("mobilebottomnavigation.settings")}</h1>
          <button
            type="button"
            className="mobile-settings-close"
            onClick={onClose}
            aria-label={t("currentuser.close")}
          >
            <span className="k-icon k-i-close" aria-hidden="true" />
          </button>
        </header>

        <section className="mobile-settings-section">
          <h2>{t("mobilebottomnavigation.workspace")}</h2>
          {dataItems
            .filter((item) => item.visible)
            .map((item) => (
              <SettingsItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                onClick={() => navigateAndClose(item.path)}
              />
            ))}
          <SettingsItem
            icon="bell"
            label={t("homepageuser.notifications")}
            onClick={() => navigateAndClose("/notifications")}
          />
          <SettingsItem
            icon="clock"
            label={t("header.schedule")}
            onClick={() => navigateAndClose("/schedule")}
          />
          <SettingsItem
            icon="connector"
            label={t("header.connection")}
            onClick={() => navigateAndClose("/connection")}
          />
        </section>

        <section className="mobile-settings-section">
          <h2>{t("mobilebottomnavigation.settings")}</h2>
          <SettingsItem
            icon="user"
            label={t("accountmang.accountmang.account")}
            onClick={() => navigateAndClose("/accountmanagement")}
          />
          {canAccessUsers(currentUser) && (
            <>
              <SettingsItem
                icon="user"
                label={t("currentuser.users")}
                onClick={() => navigateAndClose("/users")}
              />
              <SettingsItem
                icon="group"
                label={t("currentuser.groups")}
                onClick={() => navigateAndClose("/securitygroups")}
              />
            </>
          )}
          {canAccessDataConsumption(currentUser) && (
            <SettingsItem
              icon="data"
              label={t("currentuser.consumption")}
              onClick={() => navigateAndClose("/dataconsumption")}
            />
          )}
          <SettingsItem
            icon="accessibility"
            label={t("currentuser.appearance")}
            onClick={handleAppearance}
          />
          <SettingsItem
            icon="unlock"
            label={t("currentuser.changepass")}
            onClick={() => {
              onClose();
              onChangePassword();
            }}
          />
          {currentUser.Subscription?.IsRoot && (
            <SettingsItem
              icon="cells-merge-vertically"
              label={t("currentuser.sidebar")}
              onClick={() => navigateAndClose("/sidebardesigner")}
            />
          )}
          <SettingsItem
            icon="help"
            label={t("header.help")}
            to="https://help.cerebro360.ai/helpdesk/cerebro-help-2"
            external
          />
          <SettingsItem
            icon="logout"
            label={t("currentuser.logout")}
            onClick={handleLogout}
          />
        </section>
      </aside>

    </>
  );
};

export const MobileBottomNavigation = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const currentTheme = useSelector((state) => state.currentTheme);
  const navigationItems = [
    { label: t("mobilebottomnavigation.home"), path: "/", icon: "home" },
    {
      label: t("mobilebottomnavigation.reports"),
      path: "/reports",
      icon: "chart-column-clustered",
    },
    {
      label: t("mobilebottomnavigation.alerts"),
      path: "/alerts",
      icon: "warning",
    },
    { label: t("mobilebottomnavigation.ai"), path: "/#ai", icon: "reddit" },
  ];

  return (
    <>
      {settingsOpen && (
        <MobileSettingsView
          onClose={() => setSettingsOpen(false)}
          onChangePassword={() => setChangePasswordOpen(true)}
        />
      )}
      <nav
        className="mobile-bottom-navigation"
        aria-label={t("mobilebottomnavigation.primary_navigation")}
      >
        {navigationItems.map((item) => {
          const isActive =
            item.path === "/"
              ? location.pathname === "/" && location.hash !== "#ai"
              : location.pathname + location.hash === item.path;

          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={`mobile-bottom-navigation-item ${
                isActive ? "active" : ""
              }`}
              aria-current={isActive ? "page" : undefined}
            >
              <span className={`k-icon k-i-${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          className={`mobile-bottom-navigation-item ${
            settingsOpen ? "active" : ""
          }`}
          onClick={() => setSettingsOpen((open) => !open)}
          aria-expanded={settingsOpen}
          aria-controls="mobile-settings-view"
        >
          <span className="k-icon k-i-gear" aria-hidden="true" />
          <span>{t("mobilebottomnavigation.more")}</span>
        </button>
      </nav>
      {changePasswordOpen && (
        <Dialog className="change-password-form-dialog" title={null}>
          <ChangePasswordForm
            currentTheme={currentTheme}
            onCancel={() => setChangePasswordOpen(false)}
          />
        </Dialog>
      )}
    </>
  );
};
